import type { ExperienceBlockDraft, ResolvedVisual } from '@matter_extractor/shared';
import { Filter } from 'lucide-react'; // pms-design-exception: person-side screen painted by the experience theme, stroke icons kept as drawn
import type { ReactNode } from 'react';
import { READER_BLOCK_LABEL, READER_TEST_IDS, type ReaderMode } from './contracts';
import { blockAnimation } from './reader-play';

export interface BlockFrameProps {
  readonly block: ExperienceBlockDraft;
  /** `resolveTheme(theme, page.visual, block.visual)` - every token settled. */
  readonly visual: ResolvedVisual;
  readonly mode: ReaderMode;
  /** 1-based, across the whole page: what tells two Textes apart at the keyboard. */
  readonly position: number;
  readonly selected: boolean;
  readonly onSelectBlock: ((blockId: string) => void) | undefined;
  /** The editor previews only: « si @noyau · SGP » on a conditional block, else null (H73). */
  readonly conditionMark: string | null;
  readonly children: ReactNode;
}

/**
 * What EVERY block sits in: its place in the vertical flow (§9.2), its entry
 * animation (§9.3) and, in `edit`, the navy frame that says it is selected.
 *
 * The four data attributes are the whole of « alignement, largeur, espacement,
 * animation »: `reader.css` spends them, nothing here computes a style. The
 * fifth - `data-color` - carries the one theme token a block may override that
 * has to CASCADE (§9.4), so a coloured block colours its caption too.
 *
 * ## The selection is a button ON TOP, not a handler on the frame
 *
 * §9.3 asks for « un clic sur le visuel d'un bloc le sélectionne ». A click
 * handler on the frame would nest interactive content inside interactive
 * content - an Input inside a clickable div - and would leave the selection out
 * of the keyboard's reach. A transparent `<button>` covering the block takes
 * the click instead, names the block, and is reachable by Tab; the block's own
 * content goes `inert` under it, which is what stops a click from typing into
 * the field it lands on.
 *
 * ## The mark of a conditional block (§9.2, H73)
 *
 * « L'aperçu montre par défaut tous les blocs, les conditionnels marqués. » The
 * mark is the pill of the stack - a funnel and « si @noyau · SGP » - drawn in
 * the admin's grey above the block's right corner, the side the type tab does
 * not use. It is chrome and not content: it takes no pointer, and a reader
 * given no `conditionMark` (the session, the scene) never draws one.
 */
export function BlockFrame({
  block,
  visual,
  mode,
  position,
  selected,
  onSelectBlock,
  conditionMark,
  children,
}: BlockFrameProps): ReactNode {
  const editing = mode === 'edit';
  const label = READER_BLOCK_LABEL[block.type];

  return (
    <div
      className="lecteur-bloc lecteur-anim"
      data-testid={READER_TEST_IDS.block}
      data-block-id={block.id}
      data-block-type={block.type}
      data-align={visual.align}
      data-width={visual.width}
      data-spacing={visual.spacing}
      data-scale={visual.textScale}
      data-color={visual.textColor}
      data-animation={blockAnimation(block)}
      data-selected={selected ? 'true' : 'false'}
      data-conditional={conditionMark === null ? undefined : 'true'}
    >
      {conditionMark === null ? null : (
        <span className="lecteur-bloc-condition" data-testid={READER_TEST_IDS.conditionMark}>
          <Filter aria-hidden className="lecteur-bloc-condition-icone" />
          {conditionMark}
        </span>
      )}
      {editing && selected ? (
        <span className="lecteur-bloc-onglet" data-testid={READER_TEST_IDS.blockTab}>
          {label}
        </span>
      ) : null}
      {editing ? (
        <button
          type="button"
          className="lecteur-bloc-cible"
          data-testid={READER_TEST_IDS.blockTarget}
          aria-label={`Sélectionner le bloc ${position}, ${label}`}
          aria-pressed={selected}
          onClick={() => onSelectBlock?.(block.id)}
        />
      ) : null}
      <div inert={editing}>{children}</div>
    </div>
  );
}
